import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight, BookOpen } from "lucide-react";
import SEO from "@/components/SEO";
import { BLOG_POSTS } from "@/data/blog";

const SITE_URL = "https://clubamistad-madrid.com";

function fmtDate(d) {
  return new Date(d).toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function BlogListPage() {
  const posts = [...BLOG_POSTS].sort(
    (a, b) => new Date(b.meta.date) - new Date(a.meta.date)
  );

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Blog Club Amistad — Guías de esquí en familia",
    url: `${SITE_URL}/blog`,
    inLanguage: "es-ES",
    publisher: { "@id": `${SITE_URL}/#organization` },
    blogPost: posts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.meta.title,
      description: p.meta.description,
      datePublished: p.meta.date,
      url: `${SITE_URL}/blog/${p.meta.slug}`,
      image: `${SITE_URL}${p.meta.cover}`,
      author: { "@type": "Organization", name: p.meta.author },
    })),
  };

  return (
    <>
      <SEO
        title="Blog · Guías de esquí en familia | Club Amistad"
        description="Guías completas, consejos y comparativas para tu viaje de esquí con niños: qué llevar, a qué edad empezar, cuánto cuesta, mejores estaciones familiares en España."
        path="/blog"
        jsonLd={jsonLd}
      />
      <div className="pt-[76px] md:pt-[96px] bg-brand-bg min-h-screen">
        <section className="section-y">
          <div className="container-prose max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mb-14"
            >
              <p className="overline">Blog Club Amistad</p>
              <h1 className="h1-display mt-4">
                Guías y consejos para tu <em className="font-cormorant italic text-brand-accent">viaje de esquí</em> en familia.
              </h1>
              <p className="body-lg mt-6">
                56 años organizando viajes de esquí para familias nos han enseñado bastante.
                Aquí compartimos guías, comparativas y consejos prácticos para que planificar
                vuestro viaje sea (mucho) más fácil.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="blog-list">
              {posts.map((p, i) => (
                <motion.article
                  key={p.meta.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="group"
                >
                  <Link
                    to={`/blog/${p.meta.slug}`}
                    data-testid={`blog-card-${p.meta.slug}`}
                    className="block card-soft overflow-hidden hover:-translate-y-1 transition-transform"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden bg-brand-surface">
                      <img
                        src={p.meta.cover}
                        alt={p.meta.cover_alt}
                        loading="lazy"
                        className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-6 md:p-7">
                      <div className="flex items-center gap-3 text-xs text-brand-muted mb-3">
                        <span className="uppercase tracking-wider font-medium text-brand-accent">{p.meta.category}</span>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {p.meta.reading_minutes} min</span>
                      </div>
                      <h2 className="font-outfit text-2xl md:text-3xl text-brand-primary font-semibold leading-tight group-hover:text-brand-accent transition-colors">
                        {p.meta.title}
                      </h2>
                      <p className="mt-3 text-sm text-brand-ink/75 leading-relaxed line-clamp-3">
                        {p.meta.description}
                      </p>
                      <div className="mt-5 flex items-center gap-2 text-sm text-brand-primary font-medium">
                        Leer guía completa
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>

            {posts.length < 3 && (
              <div className="mt-16 rounded-2xl border border-brand-line bg-brand-surface/40 p-8 text-center">
                <BookOpen className="h-6 w-6 text-brand-accent mx-auto mb-3" />
                <p className="font-outfit text-lg text-brand-primary">
                  Más guías en preparación
                </p>
                <p className="text-sm text-brand-muted mt-2">
                  Estamos escribiendo guías sobre Cerler vs Masella, qué edad para empezar, seguros de esquí y más.
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
